import type { SVGProps } from "react";

import classNames from "classnames";
import { useMemo } from "react";
import * as React from "react";
import { requirePropertyOf } from "utils/security";

import { Tooltip } from "../Tooltip";
import { useStyles } from "./styles";

export type TIconProps = Omit<SVGProps<SVGElement>, "onClick" | "onMouseOver">
  & TBorder
  & TClickable
  & TColor
  & TDisabled
  & TSize
  & TToolTip & { onClick?: React.MouseEventHandler; onMouseOver?: React.MouseEventHandler };
type TBaseIconComponent = FC<TIconProps & { svg: React.JSX.Element }>;
type TBorder = { border?: boolean };
type TClickable = { clickable?: boolean };
type TColor = { color?: string };
type TDisabled = { disabled?: boolean };

type TSize = { size?: "large" | "medium" | "small" | "xl" | number };

type TToolTip = { tooltip?: React.ReactNode; tooltipClassName?: string };

export const isIcon = (icon: unknown): icon is IconComponent => {
  return !!icon && !!(icon as IconComponent).isIconComponent;
};

const Icon: TBaseIconComponent = ({
  border = false,
  className,
  onClick,
  clickable = !!onClick,
  color = "currentColor",
  disabled = false,
  onMouseOver,
  ref,
  size,
  svg,
  tooltip,
  tooltipClassName,
  ...svgProps
}) => {
  const SVG = useMemo(() => {
    return React.Children.map(svg, (child) => React.cloneElement(child, svgProps));
  }, [svg, svgProps]);
  const classes = useStyles({ color, size: typeof size === "number" ? size : undefined });

  const sizeClassName = useMemo(() => {
    if (typeof size === "number") {
      return classes.customSize;
    }
    return size ? requirePropertyOf(classes, size) : undefined;
  }, [size, classes]);

  const component = (
    <div
      className={classNames(
        classes.icon,
        className,
        sizeClassName,
        { [classes.border]: border, [classes.clickable]: clickable },
        disabled ? classes.disabled : "",
      )}
      onClick={onClick}
      onMouseOver={onMouseOver}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      {SVG}
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip className={tooltipClassName} content={tooltip} delayShow={700}>
        {component}
      </Tooltip>
    );
  }

  return component;
};

export const makeIcon = (svg: React.JSX.Element): IconComponent => {
  const SVGIcon: IconComponent = (props) => {
    return <Icon {...props} svg={svg} />;
  };
  SVGIcon.isIconComponent = true;
  return SVGIcon;
};
